import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ProductMillSetup } from "../models/ProductMillSetup";
import {
	removeProductMillSetup,
	upsertProductMillSetup,
} from "../repositories/ProductMillSetupRepository";
import { PRODUCT_MILL_SETUP_QUERY_KEYS } from "./useProductMillSetup";

interface MutationOptions {
	onSuccess?: (data?: ProductMillSetup) => void;
	onError?: (error: Error) => void;
}

export const useUpsertProductMillSetup = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (setup: ProductMillSetup) => {
			return await upsertProductMillSetup(setup);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_MILL_SETUP_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertMillSetup: mutation.mutate,
		upsertMillSetupAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteProductMillSetup = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeProductMillSetup(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_MILL_SETUP_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteMillSetup: mutation.mutate,
		deleteMillSetupAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
